import { useMemo } from 'react'
import * as Mtds from '@mattilsynet/design/react'
import { PlusIcon } from '@phosphor-icons/react'
import {
  getCurrentDate,
  getSortedDuedates,
  toCompactDuedateString,
  toLocaleDateString
} from '../../utils/dateUtils'
import styles from './DuedateTimeline.module.css'
import { PillTag } from '../pill-tag/PillTag'

type DuedateTimelineProps = {
  duedates: string[] | null | undefined
  isPending?: boolean
  onAddDuedate?: () => void
  onRemoveDuedate?: (duedate: string) => void
}

export const DuedateTimeline = ({
  duedates,
  isPending = false,
  onAddDuedate,
  onRemoveDuedate
}: DuedateTimelineProps) => {
  const sortedDuedates = useMemo(() => getSortedDuedates(duedates), [duedates])

  const currentDate = getCurrentDate()
  const currentDuedate = useMemo(
    () => sortedDuedates.find((duedate) => duedate >= currentDate),
    [currentDate, sortedDuedates]
  )

  if (sortedDuedates.length === 0 && onAddDuedate == null) return null

  return (
    <Mtds.Flex data-gap='2' data-align='center'>
      {sortedDuedates.map((duedate) => {
        const isCurrent = duedate === currentDuedate
        const isExpired = duedate < currentDate
        const fullDate = toLocaleDateString(duedate) ?? duedate
        const content = (
          <span data-nowrap className={isExpired ? styles.expired : undefined}>
            {toCompactDuedateString(duedate)}
          </span>
        )
        const chipProps = {
          'data-color': isCurrent ? 'success' : 'neutral',
          'aria-current': isCurrent ? ('date' as const) : undefined,
          className: isCurrent
            ? `${styles.filled} ${styles.mainText}`
            : isExpired
              ? `${styles.filled} ${styles.mainText}`
              : `${styles.outlined} ${styles.mainText}`
        } as const

        if (onRemoveDuedate == null) {
          return (
            <PillTag
              key={duedate}
              color={isCurrent ? 'success' : 'neutral'}
              appearance={isCurrent || isExpired ? 'filled' : 'outlined'}
              textColor='main'
              aria-current={chipProps['aria-current']}
              title={fullDate}
            >
              {content}
            </PillTag>
          )
        }

        return (
          <Mtds.Chip
            key={duedate}
            as='button'
            type='button'
            data-removable
            disabled={isPending}
            aria-label={`Fjern fristen ${fullDate}`}
            title={fullDate}
            onClick={() => onRemoveDuedate(duedate)}
            {...chipProps}
          >
            {content}
          </Mtds.Chip>
        )
      })}
      {onAddDuedate != null && (
        <Mtds.Chip
          as='button'
          type='button'
          data-color='neutral'
          disabled={isPending}
          aria-label='Legg til frist'
          onClick={onAddDuedate}
          className={`${styles.outlined} ${styles.mainText}`}
        >
          <PlusIcon />
        </Mtds.Chip>
      )}
    </Mtds.Flex>
  )
}
